import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { TotalRevenueSegmentationBreakdownItem } from 'lumen/models';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import type { Classification } from 'lumen/utils/constants/total-revenue-constants';
import safeDivide from 'lumen/utils/numeric/safe-divide';

export default class TotalRevenueSegmentationBreakdownReference extends Model {
  [Type] = 'total-revenue/segmentation-breakdown/reference' as const;

  @belongsTo('total-revenue/segmentation-breakdown/item', {
    async: false,
    inverse: 'referenceValues',
  })
  declare totalRevenueItem: TotalRevenueSegmentationBreakdownItem;

  @attr('string') declare classification: Classification;
  @attr('number') declare nonRoomPremium: number;
  @attr('number') declare nrOfRooms: number;
  @attr('hoteldate')
  declare referenceFromDate: NonNullable<HoteldateTransformReturn>;
  @attr declare referenceType: string;
  @attr('number') declare totalNonRoomRevenue: number;
  @attr('number') declare totalRevenue: number;
  @attr('number') declare totalRoomRevenue: number;

  @service('total-revenue')
  declare totalRevenueService: Services['total-revenue'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedNrOfRoomsChange() {
    return this.totalRevenueService.formatChange(this.nrOfRoomsChange);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedNrOfRoomsRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.nrOfRoomsRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalNonRoomRevenuePerRoomNightChange() {
    return this.totalRevenueService.formatChange(
      this.totalNonRoomRevenuePerRoomNightChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalNonRoomRevenuePerRoomNightRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.totalNonRoomRevenuePerRoomNightRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenueChange() {
    return this.totalRevenueService.formatChange(this.totalRevenueChange);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenuePerRoomNightChange() {
    return this.totalRevenueService.formatChange(
      this.totalRevenuePerRoomNightChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenuePerRoomNightRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.totalRevenuePerRoomNightRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenueRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.totalRevenueRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRoomRevenuePerRoomNightChange() {
    return this.totalRevenueService.formatChange(
      this.totalRoomRevenuePerRoomNightChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRoomRevenuePerRoomNightRelativeChange() {
    return this.totalRevenueService.formatRelativeChange(
      this.totalRoomRevenuePerRoomNightRelativeChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get nrOfRoomsChange() {
    return this.totalRevenueService.calculateChange(
      this.totalRevenueItem.nrOfRooms,
      this.nrOfRooms,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get nrOfRoomsRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.totalRevenueItem.nrOfRooms,
      this.nrOfRooms,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenuePerRoomNight() {
    return safeDivide(this.totalNonRoomRevenue, this.nrOfRooms);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenuePerRoomNightChange() {
    return this.totalRevenueService.calculateChange(
      this.totalRevenueItem.totalNonRoomRevenuePerRoomNight,
      this.totalNonRoomRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenuePerRoomNightRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.totalRevenueItem.totalNonRoomRevenuePerRoomNight,
      this.totalNonRoomRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenueChange() {
    return this.totalRevenueService.calculateChange(
      this.totalRevenueItem.totalRevenue,
      this.totalRevenue,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenuePerRoomNight() {
    return safeDivide(this.totalRevenue, this.nrOfRooms);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenuePerRoomNightChange() {
    return this.totalRevenueService.calculateChange(
      this.totalRevenueItem.totalRevenuePerRoomNight,
      this.totalRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenuePerRoomNightRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.totalRevenueItem.totalRevenuePerRoomNight,
      this.totalRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenueRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.totalRevenueItem.totalRevenue,
      this.totalRevenue,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenuePerRoomNight() {
    return safeDivide(this.totalRoomRevenue, this.nrOfRooms);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenuePerRoomNightChange() {
    return this.totalRevenueService.calculateChange(
      this.totalRevenueItem.totalRoomRevenuePerRoomNight,
      this.totalRoomRevenuePerRoomNight,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenuePerRoomNightRelativeChange() {
    return this.totalRevenueService.calculateRelativeChange(
      this.totalRevenueItem.totalRoomRevenuePerRoomNight,
      this.totalRoomRevenuePerRoomNight,
    );
  }
}
